import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { Select } from 'primeng/select';
import { Fluid } from 'primeng/fluid';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { Company, RealmConfig } from '@/lib-backend';

type Mode = 'select' | 'create';

@Component({
    selector: 'app-reassign-company-dialog',
    standalone: true,
    imports: [FormsModule, Button, Dialog, InputText, Textarea, Select, Fluid],
    templateUrl: './reassign-company-dialog.html',
})
export class ReassignCompanyDialog {
    protected readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);

    readonly visible = model.required<boolean>();
    readonly realm = input.required<RealmConfig>();
    readonly reassigned = output<RealmConfig>();

    protected readonly mode = signal<Mode>('select');

    // Select-mode state
    protected readonly selectedCompanyId = signal<string | null>(null);

    // Create-mode state
    protected readonly newName = signal('');
    protected readonly newCountryCode = signal<string | null>(null);
    protected readonly newAddress = signal('');
    protected readonly newRegDetails = signal('');

    protected readonly saving = signal(false);

    // Field errors
    protected readonly companyError = signal<string | null>(null);
    protected readonly nameError = signal<string | null>(null);
    protected readonly countryError = signal<string | null>(null);
    protected readonly addressError = signal<string | null>(null);
    protected readonly regError = signal<string | null>(null);
    protected readonly generalError = signal<string | null>(null);

    protected readonly companyOptions = computed(() =>
        this.companyService.companies()
            .filter(c => c.DeletedAt === null)
            .map(c => ({ label: this.companyLabel(c), value: c.Id }))
    );

    protected readonly countryOptions: { label: string; value: string }[] =
        this.countryService.getAll().map(c => ({ label: `${c.name} (${c.code})`, value: c.code }));

    protected readonly hasChange = computed(() => {
        if (this.mode() === 'create') {
            return this.newName().trim().length > 0;
        }
        const id = this.selectedCompanyId();
        return !!id && id !== this.realm().CompanyId;
    });

    constructor() {
        effect(() => {
            if (this.visible()) this.resetForm();
        });
    }

    private companyLabel(c: Company): string {
        const country = this.countryService.getName(c.CountryCode);
        return country ? `${c.Name} — ${country}` : c.Name;
    }

    private resetForm(): void {
        this.mode.set('select');
        this.selectedCompanyId.set(this.realm().CompanyId);
        this.newName.set('');
        this.newCountryCode.set(null);
        this.newAddress.set('');
        this.newRegDetails.set('');
        this.clearErrors();
        if (this.companyService.companies().length === 0) {
            this.companyService.loadCompanies();
        }
    }

    private clearErrors(): void {
        this.companyError.set(null);
        this.nameError.set(null);
        this.countryError.set(null);
        this.addressError.set(null);
        this.regError.set(null);
        this.generalError.set(null);
    }

    protected toggleMode(mode: Mode): void {
        this.mode.set(mode);
        this.clearErrors();
    }

    protected close(): void {
        this.visible.set(false);
    }

    protected async confirm(): Promise<void> {
        this.clearErrors();

        let companyId: string | null;
        if (this.mode() === 'select') {
            companyId = this.selectedCompanyId();
            if (!companyId) {
                this.companyError.set('Select a company');
                return;
            }
            if (companyId === this.realm().CompanyId) return;
        } else {
            companyId = await this.createInlineCompany();
            if (!companyId) return;
        }

        this.saving.set(true);
        try {
            const outcome = await this.companyService.reassignRealm(this.realm().RealmId, companyId);
            if (outcome.result === 'Ok') {
                this.reassigned.emit(outcome.realmConfig);
                this.close();
                return;
            }

            // Reassign failed — keep dialog open. If we just created a new company,
            // switch to select mode with it pre‑selected so the user doesn't try
            // to create it again.
            if (this.mode() === 'create') {
                this.mode.set('select');
                this.selectedCompanyId.set(companyId);
            }

            switch (outcome.result) {
                case 'CompanyNotFound':
                case 'CompanyDeleted':
                    this.companyError.set('Selected company is no longer available.');
                    await this.companyService.loadCompanies();
                    break;
                case 'RealmNotFound':
                    this.generalError.set('This website no longer exists.');
                    break;
                case 'InvalidActivePaymentMethod':
                    this.companyError.set('This website uses a payment method (e.g. Stripe) that the target company does not have configured.');
                    break;
                default:
                    this.generalError.set('Failed to reassign. Please try again.');
            }
        } finally {
            this.saving.set(false);
        }
    }

    /**
     * Create a Company inline. Returns its ID on success, or null on validation /
     * server error (errors are surfaced via field signals; dialog stays open).
     */
    private async createInlineCompany(): Promise<string | null> {
        const name = this.newName().trim();
        const country = this.newCountryCode();
        const address = this.newAddress().trim();
        const reg = this.newRegDetails().trim();

        let invalid = false;
        if (!name) { this.nameError.set('Name is required'); invalid = true; }
        if (!country) { this.countryError.set('Country is required'); invalid = true; }
        if (!address) { this.addressError.set('Address is required'); invalid = true; }
        if (!reg) { this.regError.set('Registration details are required'); invalid = true; }
        if (invalid) return null;

        this.saving.set(true);
        try {
            const outcome = await this.companyService.createCompany({
                Name: name,
                Address: address,
                RegistrationDetails: reg,
                Country: country!,
                Stripe: { IsEnabled: false, PublishableKey: '', SecretKey: '', WebhookSecret: '' },
                LiveChatConfig: { GroupId: null },
            });
            if (outcome.result === 'Ok') return outcome.company.Id;

            if (outcome.result === 'NameAlreadyUsed') {
                this.nameError.set('A company with this name already exists');
            } else {
                this.generalError.set('Failed to create company');
            }
            return null;
        } finally {
            this.saving.set(false);
        }
    }
}
